import React, { useEffect, useRef, useCallback } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useSelector, useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import { Corner, WindowState } from '$root/utils/enums';
import SizeNE from '$root/graphics/ne.svg';
import SizeNW from '$root/graphics/nw.svg';
import SizeSE from '$root/graphics/se.svg';
import SizeSW from '$root/graphics/sw.svg';

import { setSelectedPlan, setPlanViewState } from '$root/store/actions';
import { useAuth } from '$root/contexts/auth';
import PlanViewPositionSelect from '$root/components/PlanViewPositionSelect';
import { HTMLSelect, Button } from '@blueprintjs/core';
import { Tooltip2 } from '@blueprintjs/popover2';
import { Position, Colors } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';


const Frame = styled.div.attrs(props => ({
    darkMode: props.darkMode || false,
    maximized: props.maximized || false,
}))`
    //min-width: 256px;
    //min-height: 160px;
    z-index: 5;
    
    display: flex;
    flex-direction: column;
    
    border: 1px solid black;
    border-radius: 4px;
    color: ${ props => props.darkMode ? Colors.DARK_GRAY5 : Colors.GRAY5 };
    background-color: ${ props => props.darkMode ? Colors.GRAY5 : Colors.DARK_GRAY5 };
    
    opacity: ${ props => props.maximized ? 1 : 0.65 };
    transition: all 0.25s ease-in-out;
    
    &:hover {
        box-shadow: 0px 0px 8px 2px #808080;
        opacity: 1.0;
    }
`;


const FrameToolbar = styled.div.attrs(props => ({

}))`
    display: flex;
    flex-direction: row;
    margin-bottom: 5px;
    & > * {
        margin-left: 8px;
    }
    & :nth-child(1) {
        margin-left: 0;
    }
`;


export default function PlanViewFrame(props)
{
    const { style, sizableCorner, children } = props;
    const { keepalive } = useAuth();
    const dispatch = useDispatch();
    const planViewState = useSelector(state => state.ui.planView.state);
    const planViewPosition = useSelector(state => state.ui.planView.position);
    const availablePlans = useSelector(state => state.project.availablePlans);
    const selectedPlan = useSelector(state => state.project.selectedPlan);
    const uiDarkMode = useSelector(state => state.ui.darkMode);


    let resizeIcon;
    let resizeIconStyle;

    switch (planViewPosition) {
    case Corner.SW:
        resizeIconStyle = {
            position:'absolute',
            top:0,
            right:0,
            cursor:'ne-resize',
        };
        resizeIcon = SizeNE;
        break;
    case Corner.SE:
        resizeIconStyle = {
            position:'absolute',
            top:0,
            left:0,
            cursor:'nw-resize',
        };
        resizeIcon = SizeNW;
        break;
    case Corner.NW:
        resizeIconStyle = {
            position:'absolute',
            bottom:0,
            right:0,
            cursor:'se-resize',
        };
        resizeIcon = SizeSE;
        break;
    case Corner.NE:
        resizeIconStyle = {
            position:'absolute',
            bottom:0,
            left:0,
            cursor:'sw-resize',
        };
        resizeIcon = SizeSW;
        break;
    }


    const availablePlanOptions = (availablePlans || []).map(plan => ({
        label: plan.name,
        value: plan.id
    }));


    const onPlanSelect = useCallback((evt) => {
        const plan = (availablePlans || []).find(p => p.id === evt.currentTarget.value * 1);
        dispatch(setSelectedPlan(plan))
    }, [availablePlans]);


    const onChangePlanViewState = (state) => {
        keepalive();
        dispatch(setPlanViewState(state));
    }


    const isMaximized = planViewState === WindowState.MAXIMIZED;
    const isMinimized = planViewState === WindowState.MINIMIZED;
    const isNormal = planViewState === WindowState.NORMAL;

    return (
        <Frame style={{...style, }}
               darkMode={uiDarkMode}
               maximized={isMaximized}
        >
            {/*{ isNormal &&*/}
            {/*    <img src={resizeIcon} width={12} height={12}*/}
            {/*         style={resizeIconStyle}/>*/}
            {/*}*/}
            <div style={{
                padding: 5,
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
            }}>
                <FrameToolbar>
                    {!isMinimized &&
                        <HTMLSelect options={availablePlanOptions}
                                    fill
                                    value={selectedPlan?.id || 0}
                                    onChange={onPlanSelect}
                        />
                    }
                    {isMinimized &&
                        <div style={{width:'100%'}} />
                    }
                    {isNormal &&
                        <Tooltip2 content={'Elhelyezés'} disabled={window.isTablet}>
                            <PlanViewPositionSelect/>
                        </Tooltip2>
                    }
                    {isNormal &&
                        <Tooltip2 content={'Kis méret'} disabled={window.isTablet}>
                            <Button icon={IconNames.CHEVRON_DOWN}
                                    onClick={() => onChangePlanViewState(WindowState.MINIMIZED)}
                            />
                        </Tooltip2>
                    }
                    {isNormal &&
                        <Tooltip2 content={'Nagy méret'} disabled={window.isTablet}>
                            <Button icon={IconNames.MAXIMIZE}
                                    onClick={() => onChangePlanViewState(WindowState.MAXIMIZED)}
                            />
                        </Tooltip2>
                    }
                    {!isNormal &&
                        <Tooltip2 content={'Normal'} disabled={window.isTablet}>
                            <Button icon={IconNames.MODAL}
                                    onClick={() => onChangePlanViewState(WindowState.NORMAL)}
                            />
                        </Tooltip2>
                    }
                </FrameToolbar>
                <div style={{
                    width:'100%',
                    height:'100%',
                    borderRadius:'3px',
                }}>
                    {children}
                </div>
            </div>
        </Frame>
    );
}